import React, { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { matchIndicator, indicatorCellStyle, normInd, DEFAULT_FOMO_INDICATORS } from "../lib/fomo";
import { FomoKey } from "./BookingChrome";
import { mergeBranding } from "../lib/templates";

const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function parse(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export default function PublicCalendar({ dates, attraction, onPick, selected }) {
  const byDate = useMemo(() => {
    const m = {};
    (dates || []).forEach((d) => { m[d.date] = d; });
    return m;
  }, [dates]);

  const todayStr = ymd(new Date());

  const initial = useMemo(() => {
    const sorted = (dates || []).map((d) => d.date).sort();
    const upcoming = sorted.find((s) => s >= todayStr);
    const pick = upcoming || sorted[sorted.length - 1];
    const base = pick ? parse(pick) : new Date();
    return { y: base.getFullYear(), m: base.getMonth() };
  }, [dates]); // eslint-disable-line react-hooks/exhaustive-deps

  const [view, setView] = useState(initial);

  const grid = useMemo(() => {
    const first = new Date(view.y, view.m, 1);
    const startOffset = (first.getDay() + 6) % 7;
    const start = new Date(view.y, view.m, 1 - startOffset);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const dt = new Date(start);
      dt.setDate(start.getDate() + i);
      cells.push(dt);
    }
    return cells;
  }, [view]);

  const branding = mergeBranding(attraction?.branding);
  const accent = branding.accent_color || "#dc2626";
  const fomoOn = !!branding.fomo_enabled;
  const indicators = (branding.fomo_indicators && branding.fomo_indicators.length) ? branding.fomo_indicators : DEFAULT_FOMO_INDICATORS;

  const gridStr = new Set(grid.map(ymd));
  const future = (dates || []).map((d) => d.date)
    .filter((s) => !gridStr.has(s) && s >= todayStr).sort();
  const nudge = future.length ? parse(future[0]) : null;

  const prev = () => setView((v) => { const d = new Date(v.y, v.m - 1, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const next = () => setView((v) => { const d = new Date(v.y, v.m + 1, 1); return { y: d.getFullYear(), m: d.getMonth() }; });

  return (
    <div className="pc" data-testid="public-calendar">
      <div className="flex items-center justify-between mb-4">
        <button onClick={prev} className="pc-nav" data-testid="cal-prev" aria-label="Previous month"><ChevronLeft size={18} /></button>
        <div className="pc-title" data-testid="cal-title">{MONTHS[view.m]} {view.y}</div>
        <button onClick={next} className="pc-nav" data-testid="cal-next" aria-label="Next month"><ChevronRight size={18} /></button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {WD.map((w) => <div key={w} className="pc-wd">{w}</div>)}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {grid.map((dt, i) => {
          const ds = ymd(dt);
          const info = byDate[ds];
          const inMonth = dt.getMonth() === view.m;
          if (!info) {
            return <div key={i} className={`pc-cell pc-empty ${inMonth ? "" : "pc-out"}`}>{dt.getDate()}</div>;
          }
          const isSel = selected === ds;
          if (info.is_past || ds < todayStr) {
            return (
              <div key={i} className="pc-cell pc-past" data-testid={`cal-date-${ds}`} aria-disabled="true" title="This date has passed">
                <span className="pc-num">{dt.getDate()}</span>
                <span className="pc-tag">Closed</span>
              </div>
            );
          }
          if (info.sold_out) {
            const ind = fomoOn ? matchIndicator(indicators, 100) : null;
            const style = ind ? indicatorCellStyle(ind) : undefined;
            return (
              <div key={i} className="pc-cell pc-sold" data-testid={`cal-date-${ds}`} style={style}>
                <span className="pc-num">{dt.getDate()}</span>
                <span className="pc-tag" style={ind ? { color: normInd(ind).label_color } : undefined}>{ind?.label || "Sold out"}</span>
              </div>
            );
          }
          const dcap = info.fomo_capacity != null ? info.fomo_capacity : info.max_capacity;
          const dsold = info.fomo_sold != null ? info.fomo_sold : info.tickets_sold;
          const fill = Math.min(99, Math.round(100 * dsold / Math.max(1, dcap)));
          const ind = fomoOn ? matchIndicator(indicators, fill) : null;
          const style = ind && !isSel ? indicatorCellStyle(ind) : undefined;
          return (
            <button key={i} onClick={() => onPick(info)} data-testid={`cal-date-${ds}`}
                    className={`pc-cell pc-open ${isSel ? "pc-selected" : ""}`} style={style}>
              <span className="pc-num">{dt.getDate()}</span>
              {ind
                ? <span className="pc-tag" style={isSel ? undefined : { color: normInd(ind).label_color }}>{ind.label}</span>
                : <span className="pc-tag" style={isSel ? undefined : { color: accent }}>Book</span>}
            </button>
          );
        })}
      </div>

      {fomoOn && <FomoKey indicators={indicators} />}

      {nudge && (
        <button onClick={() => setView({ y: nudge.getFullYear(), m: nudge.getMonth() })}
                className="pc-nudge" data-testid="cal-nudge">
          More dates available in {MONTHS[nudge.getMonth()]} <ChevronRight size={14} className="inline -mt-0.5" />
        </button>
      )}
    </div>
  );
}
